import PropTypes from 'prop-types'
import RadioComponent from '../../../atoms/radio'
import { contextMenuControlOptions, contextMenuSizeOptions } from '../constants'
import { RootContainer } from '../styled-components'
import { ContextMenuContext } from './context-menu-context'
import { forwardRef, useEffect, useRef, useState } from 'react'

const ContextMenuRoot = forwardRef(
  (
    {
      control = null,
      size = contextMenuSizeOptions.DESKTOP,
      radioProps = null,
      checkBoxProps = null,
      handleSelect = null,
      noEmptyField = false,
      isLoading = false,
      setIsOpen: setIsOpenProp = null,
      isOpen: isOpenProp = null,
      children,
      isSingle = false,
      selectedItemsProp = null,
      setSelectedItemsProp = null,
      defaultValue = [],
      ...props
    },
    forwardedRef
  ) => {
    const [isOpenNative, setIsOpenNative] = useState(false)
    const [selectedItemsNative, setSelectedItemsNative] = useState(defaultValue)
    const [selectedRadio, setSelectedRadio] = useState(null)

    // handle with outside control
    const selectedItems = selectedItemsProp ?? selectedItemsNative
    const setSelectedItems = setSelectedItemsProp ?? setSelectedItemsNative

    const containerRef = useRef(null)

    const isOpen = isOpenProp ?? isOpenNative
    const setIsOpen = setIsOpenProp ?? setIsOpenNative

    const handleAddSingleItem = (item) => {
      setSelectedItems([item])
      handleSelect && handleSelect([item])
    }

    const handleAddMultiItem = (item) => {
      const updatedItem = [...selectedItems, item]
      setSelectedItems(updatedItem)

      handleSelect && handleSelect(updatedItem)
    }

    const handleAddItem = isSingle ? handleAddSingleItem : handleAddMultiItem

    const handleRemoveItem = (item) => {
      const updatedItems = selectedItems.filter(
        (selectedItem) => selectedItem.id !== item.id
      )
      setSelectedItems(updatedItems)

      handleSelect && handleSelect(updatedItems)
    }

    const handleIsOpen = () => {
      setIsOpen((prev) => !prev)
    }

    const handleCheckboxChange = (item, checked) => {
      setSelectedItems((prev) =>
        !checked
          ? [...prev, item]
          : prev.filter((prevItem) => prevItem.id !== item.id)
      )
    }

    const handleRadioChange = (item) => {
      setSelectedItems([item])
    }

    useEffect(() => {
      const ref = forwardedRef ?? containerRef
      const handleClickOutside = (event) => {
        if (ref.current && !ref.current.contains(event.target)) {
          setIsOpen(false)
        }
      }

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside)
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [isOpen])

    return (
      <ContextMenuContext.Provider
        value={{
          isOpen,
          handleIsOpen,
          control,
          size,
          selectedItems,
          handleRemoveItem,
          handleAddItem,
          noEmptyField,
          handleCheckboxChange,
          checkBoxProps,
          isLoading,
          handleRadioChange
        }}
      >
        <RootContainer ref={containerRef} {...props}>
          {control === contextMenuControlOptions.RADIO ? (
            <RadioComponent.Root
              style={{ gap: 0 }}
              onValueChange={(id) => setSelectedRadio(id)}
              checked={selectedRadio}
              {...radioProps}
            >
              {children}
            </RadioComponent.Root>
          ) : (
            children
          )}
        </RootContainer>
      </ContextMenuContext.Provider>
    )
  }
)

ContextMenuRoot.displayName = 'ContextMenuRoot'

ContextMenuRoot.propTypes = {
  control: PropTypes.oneOf(Object.values(contextMenuControlOptions)),
  size: PropTypes.oneOf(Object.values(contextMenuSizeOptions)),
  radioProps: PropTypes.object,
  checkBoxProps: PropTypes.object,
  handleSelect: PropTypes.func,
  noEmptyField: PropTypes.bool,
  isLoading: PropTypes.bool,
  setIsOpen: PropTypes.func,
  isOpen: PropTypes.bool
}

export default ContextMenuRoot
